import { BondRequestPreview } from "@portal/api/bond-request/agent";
import type { DynamicDataTableColumn } from "@portal/components/ui/DynamicDataTable/types";
import { RowActionButtons } from "@portal/components/ui/row-action-buttons/RowActionButtons";
import { getStatusTagByStatus, statusDisplayLabelMap } from "@portal/components/ui/StatusTag";
import { DateTime } from "luxon";
import { bondRequestTypeLabelMap } from "../../../constants";
import { formatBondAmount, formatDateForCsv, formatEmpty } from "../../../utils";
import { AdminAssigneeControls } from "../shared/AdminAssigneeControls";

type AdminBondRequestPreview = BondRequestPreview & { underwriterName: string };

export const getRequestsTableColumns = (
	isMinimalUIEnabled: boolean,
): readonly DynamicDataTableColumn<AdminBondRequestPreview>[] => [
	{
		id: "underwriter",
		accessorKey: "underwriterName",
		headerFilter: { type: "checkbox" },
		cellStyle: { noTruncate: true },
		csv: { rowObjectKey: "underwriterName", fileColumnHeader: "Underwriter" },
		size: 60,
		enableSorting: true,
		cell: ({ row }) =>
			row.original.underwriterUserId ? (
				<RowActionButtons alwaysShow={true}>
					<AdminAssigneeControls
						assignedEntity={{ entityType: "quote", entityId: row.original.id }}
						currentAssignee={row.original.underwriterUserId}
					/>
				</RowActionButtons>
			) : null,
	},
	{
		id: "status",
		accessorFn: (row) => statusDisplayLabelMap[row.status] ?? row.status,
		header: "Status",
		size: 205,
		minSize: 205,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
		cell: ({ row }) => getStatusTagByStatus(row.original.status),
	},
	{
		id: "type",
		header: "Type",
		accessorFn: ({ suretySubType }) =>
			formatEmpty(suretySubType ? bondRequestTypeLabelMap[suretySubType] : undefined),
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "principal_name",
		header: "Principal",
		accessorFn: ({ principalName }) => formatEmpty(principalName),
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "amount",
		accessorKey: "amount",
		header: "Amount",
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		cell: ({ row }) => formatBondAmount(row.original.amount),
	},
	{
		id: "description",
		header: isMinimalUIEnabled ? "Project Name" : "Description",
		accessorFn: ({ description, name }) => formatEmpty(isMinimalUIEnabled ? name : description),
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
	},
	{
		id: "account_name",
		header: "Account",
		accessorFn: ({ accountName }) => formatEmpty(accountName),
		size: 180,
		minSize: 130,
		enableResizing: true,
		enableSorting: true,
		headerFilter: { type: "checkbox" },
	},
	{
		id: "submittedAt",
		header: "Submitted",
		accessorFn: ({ submittedAt }) =>
			submittedAt !== undefined ? DateTime.fromISO(submittedAt).toJSDate() : undefined,
		size: 150,
		minSize: 130,
		enableSorting: true,
		sortingFn: "datetime",
		csv: { formatValue: formatDateForCsv },
		cell: ({ row }) =>
			row.original.submittedAt !== undefined
				? DateTime.fromISO(row.original.submittedAt).toLocaleString(DateTime.DATE_MED)
				: "--",
	},
	{
		id: "entityTags",
		header: "Tags",
		accessorFn: ({ entityTags }) => entityTags?.map((tag) => tag.displayName) ?? [],
		enableResizing: true,
		enableSorting: true,
		enableHiding: true,
		headerFilter: { type: "checkbox-array" },
		cell: ({ row }) => row.original.entityTags?.map((tag) => tag.displayName).join(", ") || "--",
	},
];
